import React from 'react';
import { AxiosError } from "axios";
import { useSnackbar, VariantType } from "notistack";
export interface IException { //Objeto de exceção
    message: string,
    title: string,
    errors: string[] //Aninhe recursivamente aqui as mensagens da exceção e de suas inner exceptions
}

export interface IBadRequestError {
    propertyName: string,
    errorMessage: string
}

interface IHandleCallback<T> {
    (error: T): void
}

interface IMessage {
    success: (message: string) => void,    
    error: (message: string) => void,    
    info: (message: string) => void,    
    warning: (message: string) => void,    
    showException: (e?: AxiosError<IException> | string) => void    
    showBlobException: (e: AxiosError<Blob>) => void    
    getMessage: (e: AxiosError<IException> | string) => string   
    handleResponse: (this: IMessage, e: AxiosError) => IResponseHandle    
}

interface IResponseHandle {
    start: (this: IResponseHandle, message: IMessage, e: AxiosError) => IResponseHandle
    unauthorized: (callback: IHandleCallback<any>) => IResponseHandle
    badRequest: (callback: IHandleCallback<IBadRequestError[]>) => IResponseHandle
    forbidden: (callback: IHandleCallback<any>) => IResponseHandle
    showException: () => void    
}

function useHandle () {
    const handledRef = React.useRef(false);
    const errorRef = React.useRef<AxiosError>();
    const messageRef = React.useRef<IMessage>();

    const start = React.useCallback(function (this: IResponseHandle, message: IMessage, e: AxiosError) {
        handledRef.current = false;
        messageRef.current = message;
        errorRef.current = e;

        return this;
    }, []);

    const isResponseStatus = React.useCallback((status: number) => {
        return status === errorRef.current?.response?.status
    }, []);

    const unauthorized = React.useCallback(function (this: IResponseHandle, callback: IHandleCallback<any>) {
        if (isResponseStatus(401) && !handledRef.current) {
            callback(errorRef.current);
            handledRef.current = true;
        }

        return this;
    }, [isResponseStatus]);

    const forbidden = React.useCallback(function (this: IResponseHandle, callback: IHandleCallback<any>) {
        if (isResponseStatus(403) && !handledRef.current) {
            callback(errorRef.current);
            handledRef.current = true;
        }

        return this;
    }, [isResponseStatus])

    const badRequest = React.useCallback(function (this: IResponseHandle, callback: IHandleCallback<IBadRequestError[]>) {
        if (isResponseStatus(400) && !handledRef.current) {
            const errors = (errorRef.current?.response?.data as IBadRequestError[] || []);
            callback(errors);
            handledRef.current = true;
        }

        return this;
    }, [isResponseStatus]);

    const showException = React.useCallback((ignoreHandled?: boolean) => {
        if (ignoreHandled === true || !handledRef.current) {
            messageRef.current?.showException(errorRef?.current);
        }
    }, []);

    return React.useMemo(() => ({
        start,
        unauthorized,
        badRequest,
        forbidden,
        showException
    }), [start, badRequest, forbidden, showException, unauthorized]);
}

export default function useMessage () : IMessage {
    const snackbar = useSnackbar();
    const handle = useHandle();

    const showMessage = React.useCallback((message: string, variant: VariantType) => {
        snackbar.enqueueSnackbar(message, {
            variant
        });
    }, [snackbar]);

    const success = React.useCallback((message: string) => {
        showMessage(message, "success");
    }, [showMessage]);

    const error = React.useCallback((message: string) => {
        showMessage(message, "error");
    }, [showMessage]);

    const info = React.useCallback((message: string) => {
        showMessage(message, "info");
    }, [showMessage]);

    const warning = React.useCallback((message: string) => {
        showMessage(message, "warning");
    }, [showMessage]);

    const getMessage = React.useCallback((e?: AxiosError<IException> | string) => {
        if (typeof(e) === "string") {
            return e;
        }
        else if (e !== undefined) {
            const data = e.response?.data;

            if (data !== undefined && (data || "").toString() !== "") {
                return data.message || data.title || data.toString();
            }
            else if (e.message !== undefined && e.message !== "") {
                return e.message;
            }
            else if (e.response?.statusText !== undefined && e.response.statusText !== "") {
                return e.response.statusText;
            }
            else {
                return e.toString();
            }
        }

        return "Erro desconhecido";
    }, []);

    const showException = React.useCallback((e?: AxiosError<IException> | string) => {
        const message = getMessage(e);
        error(message);
    }, [error, getMessage]);

    const showBlobException = React.useCallback((e: AxiosError<Blob>) => {
        const blob = e.response?.data;

        if (blob instanceof Blob) {
            
            blob.text()
            .then(text => {
                const _e = {...e} as AxiosError<IException>;
                _e.response!.data = JSON.parse(text) as IException;
                showException(_e);
            });
        }
        else {
            showException(blob as any);
        }
    }, [showException]);

    const handleResponse = React.useCallback(function (this: IMessage, e: any) {
        return handle.start(this, e);
    }, [handle]);

    return React.useMemo(() => ({
        showBlobException, success, error, info, warning, showException, getMessage, handleResponse
    }), [showBlobException, error, getMessage, handleResponse, info, showException, success, warning]);
}
